import { generatePicture } from './generatePicture';
import { swiperInit, swiperDestroy } from '../swiper/swiper';
import { generateVideo } from './generateVideo';
import { GalleryPage } from '../helpers/enums';
import { trapFocus } from '../helpers/util';

export const gallery = () => {
  /* || CONSTS */
  const GALLERY_MODAL_ACTIVE = 'gallery-modal--active';
  const OVERFLOW_HIDDEN = 'overflow--hidden';
  const IMAGE_INDEX = 'image-index';
  const VIDEO = 'Video';
  const PICTURE = 'Picture';

  const body = document.querySelector('body');
  const galleryOpenCta = document.querySelectorAll<HTMLElement>('[js-gallery-open]');
  const galleryOpenCarouselCta = document.querySelectorAll<HTMLButtonElement>('[js-gallery-open-carousel]');
  const galleryGridModal = document.querySelector<HTMLElement>('[js-gallery-grid-modal]');
  const gallerySwiperModal = document.querySelector<HTMLElement>('[js-gallery-swiper-modal]');
  const galleryCloseAllCta = document.querySelectorAll<HTMLElement>('[js-gallery-close-all]');
  const galleryCloseSwiperCta = document.querySelector<HTMLElement>('[js-gallery-close-swiper]');
  const galleryImageContainer = document.querySelector<HTMLElement>('[js-gallery-image-container]');
  const gallerySwiperImageContainer = document.querySelector<HTMLElement>('[js-gallery-swiper-container]');
  const swiperElement = document.querySelector<HTMLElement>('[js-swiper-modal-element]');

  if (
    !body ||
    !galleryOpenCta ||
    !galleryGridModal ||
    !gallerySwiperModal ||
    !galleryCloseAllCta ||
    !galleryImageContainer ||
    !gallerySwiperImageContainer ||
    !swiperElement
  ) {
    return;
  }

  /* || OPEN MODAL FUNCTIONS */

  /* Open gallery swiper modal */
  const openGallerySwiperModal = (
    clickedBtn: HTMLButtonElement,
    swiperImages: NodeListOf<HTMLElement>,
    swiperVideo: HTMLElement | null = null
  ) => {
    /* Get index of clicked image. Swiper gallery will start on this slide */
    const clickedBtnIndex = Number(clickedBtn.getAttribute(IMAGE_INDEX) || 0);

    /* Show swiper modal */
    gallerySwiperModal.classList.add(GALLERY_MODAL_ACTIVE);
    galleryGridModal.classList.add(OVERFLOW_HIDDEN);

    if (swiperVideo) {
      generateSwiperSlide(swiperVideo, VIDEO);
    }

    /* Loop through list of images and create new <img> tag within modal */
    swiperImages.forEach((image) => {
      generateSwiperSlide(image);
    });

    swiperInit(swiperElement, clickedBtnIndex);

    setTimeout(() => {
      const button = gallerySwiperModal.querySelectorAll('button')[0];
      if (button) {
        button.focus();
      }
    }, 2000);

    trapFocus(gallerySwiperModal);
  };

  /* Open gallery grid modal */
  const openGalleryGridModal = (element: HTMLElement) => {
    /* Get id and images for the clicked button. Needed for city page as multiple galleries on same page */
    const galleryId = element.getAttribute('js-gallery-id');
    const galleryVideo = document.querySelector<HTMLElement>(`[js-gallery-video][js-gallery-id="${galleryId}"]`);
    const galleryImages = document.querySelectorAll<HTMLElement>(`[js-gallery-images][js-gallery-id="${galleryId}"]`);

    if (!galleryImages) {
      return;
    }

    /* Show grid modal */
    galleryGridModal.classList.add(GALLERY_MODAL_ACTIVE);
    body.classList.add(OVERFLOW_HIDDEN);

    if (galleryVideo) {
      const newElementVideo = generateVideo(galleryVideo, GalleryPage.GRID);
      galleryImageContainer.append(newElementVideo);
    }

    /* Loop through list of images and create new <img> tag within modal */
    galleryImages.forEach((image, index) => {
      /* Has video, incremenet everything by 1 */
      galleryVideo ? index++ : index;

      /* Create new button with classes. Button will be wrapped round image to make click functionality more keyboard accessible */
      const newElementButton = document.createElement('button');
      newElementButton.setAttribute(IMAGE_INDEX, index.toString());
      newElementButton.classList.add('hover--darken', 'rest--5', 'transition', 'cursor--pointer');

      const newElementPicture = generatePicture(image, GalleryPage.GRID);

      newElementButton.append(newElementPicture);
      galleryImageContainer.append(newElementButton);

      /* Clicking on any image will open the full screen swiper gallery */
      newElementButton.addEventListener('click', function () {
        pauseAllVideos();
        openGallerySwiperModal(this, galleryImages, galleryVideo);
      });
    });

    setTimeout(() => {
      const button = galleryGridModal.querySelectorAll<HTMLElement>('[js-gallery-image-container] button')[0];

      if (button) {
        button.focus();
        trapFocus(galleryGridModal);
      }
    }, 1000);
  };

  /* || CLOSE MODAL FUNCTIONS */

  /* Close gallery grid + swiper modals */
  const closeGalleryModals = () => {
    /*Destroy current Swiper*/
    swiperDestroy(swiperElement);

    /* Hide modal */
    gallerySwiperModal?.classList.remove(GALLERY_MODAL_ACTIVE);
    galleryGridModal.classList.remove(GALLERY_MODAL_ACTIVE);
    body.classList.remove(OVERFLOW_HIDDEN);

    /* Clear html of modal so that items dont duplicate when opened again */
    galleryImageContainer.innerHTML = '';
    gallerySwiperImageContainer.innerHTML = '';
  };

  /* Close gallery swiper modal */
  const closeGallerySwiperModal = () => {
    /*Destroy current Swiper*/
    swiperDestroy(swiperElement);

    /* Hide modal */
    gallerySwiperModal?.classList.remove(GALLERY_MODAL_ACTIVE);
    galleryGridModal.classList.remove(OVERFLOW_HIDDEN);

    /* Clear html of modal so that items dont duplicate when opened again */
    gallerySwiperImageContainer.innerHTML = '';
  };

  /* || EVENT LISTENERS */

  /* Add open function to each image and button */
  galleryOpenCta.forEach((cta) => {
    cta.addEventListener('click', () => {
      pauseAllVideos();
      openGalleryGridModal(cta);
    });
  });

  /* Open swiper carousel directly from main page by clicking an image */
  galleryOpenCarouselCta.forEach((cta) => {
    cta.addEventListener('click', () => {
      pauseAllVideos();

      /* Get id and images for the clicked carousel. Needed for city page as multiple galleries on same page */
      const galleryId = cta.getAttribute('js-gallery-id');
      const galleryVideo = document.querySelector<HTMLElement>(`[js-gallery-video][js-gallery-id="${galleryId}"]`);
      const galleryImages = document.querySelectorAll<HTMLElement>(`[js-gallery-images][js-gallery-id="${galleryId}"]`);

      openGallerySwiperModal(cta, galleryImages, galleryVideo);
    });
  });

  /* Add event for closing modal. There are two close buttons, one on each gallery modal */
  galleryCloseAllCta.forEach((cta) => {
    cta.addEventListener('click', () => {
      pauseAllVideos();
      closeGalleryModals();
    });
  });

  galleryCloseSwiperCta?.addEventListener('click', () => {
    pauseAllVideos();
    closeGallerySwiperModal();
  });

  /* || HELPER FUCNTIONS */

  /* Create swiper slide */
  const generateSwiperSlide = (media: HTMLElement, mediaType = PICTURE) => {
    /* Create new button with classes. Button will be wrapped round image/video to make click functionality more keyboard accessible */
    const newElementSwiperSlide = document.createElement('div');
    newElementSwiperSlide.classList.add('swiper-slide');

    /* Create new responsive image/video and append into slide div */
    let newElementMedia: HTMLElement;
    if (mediaType === VIDEO) {
      newElementMedia = generateVideo(media, GalleryPage.CAROUSEL);
    } else {
      newElementMedia = generatePicture(media, GalleryPage.CAROUSEL);
    }

    newElementSwiperSlide.append(newElementMedia);
    gallerySwiperImageContainer.append(newElementSwiperSlide);
  };
};

/* Pause all videos. Used when opening and closing modals which otherwise leads to videos playing in the background while not visible */
const pauseAllVideos = () => {
  const allVideos = document.querySelectorAll<HTMLVideoElement>('video');
  allVideos.forEach((video) => video.pause());
};
